const ADMIN_API_URL = process.env.REACT_APP_ADMIN_API_URL;
const KCP_API_URL = process.env.REACT_APP_KCP_API_URL;
export const GIFTLY_URL = process.env.REACT_APP_GIFTLY_URL;
export const BOA_DASHBOARD_URL = process.env.REACT_APP_BOA_DASHBOARD_URL;

const makeAdminURL = (pathname) => {
    return `${ADMIN_API_URL}${pathname}`;
};

const makeAdminURLV2 = (pathname) => {
    return `${ADMIN_API_URL}/v2${pathname}`;
};

/** Returns (params) => baseUrl + '?' + URLSearchParams from param keys (skips null/empty). */
const withQueryParams = (baseUrl, paramKeys) => (params) => {
    const search = paramKeys.reduce((acc, k) => {
        if (params[k] != null && params[k] !== '') acc[k] = String(params[k]);
        return acc;
    }, {});
    const qs = new URLSearchParams(search).toString();
    return qs ? `${baseUrl}?${qs}` : baseUrl;
};

const makeKcpURL = (pathname) => {
    return `${KCP_API_URL}${pathname}`;
};

export const adminAPI = {
    adminUser: {
        me: makeAdminURL('/admin-users/me'),
        mfa: {
            setup: makeAdminURL('/admin-users/mfa-setup'),
            verify: makeAdminURL('/admin-users/mfa-verify'),
            ipWhitelist: makeAdminURL('/admin-users/ip-whitelist')
        }
    },
    auth: {
        signIn: makeAdminURL('/auth'),
        logout: makeAdminURL('/auth/logout'),
        google: {
            redirectUrl: makeAdminURL('/auth/google'),
            callback: makeAdminURL('/auth/google')
        }
    },
    system: {
        awa: makeAdminURL('/system/awa'),
        cacheSync: makeAdminURL('/v2/accounts/cache-sync'),
        users: makeAdminURL('/admin-users'),
        ipWhitelist: makeAdminURL('/ip-whitelist'),
        esMigrations: makeAdminURL('/system/es-migrations'),
        setupWebhooks: makeAdminURL('/system/setup-webhooks'),
        scopeSync: makeAdminURL('/system/scope-sync'),
        priorityCalculator: (campaignId) =>
            makeAdminURL(`/system/priority-calculator${campaignId ? `/${campaignId}` : ''}`),
        basisDSP: {
            settings: {
                get: makeAdminURL('/system/dsp-basis/settings'),
                update: makeAdminURL('/system/dsp-basis/settings'),
                getHistoryLog: makeAdminURL('/system/dsp-basis/settings/history-log')
            }
        },
        cron: {
            run: makeAdminURL('/system/cron/run')
        },
        scriptTagDomains: {
            list: makeAdminURL('/system/script-tag-domains'),
            create: makeAdminURL('/system/script-tag-domains'),
            toggleActive: (id) => makeAdminURL(`/system/script-tag-domains/${id}/toggle-active`),
            delete: (id) => makeAdminURL(`/system/script-tag-domains/${id}`),
            listAccounts: (id) => makeAdminURL(`/system/script-tag-domains/${id}/accounts`),
            updateAccounts: makeAdminURL('/system/script-tag-domains/update-accounts')
        },
        tripleWhaleAttribution: {
            columns: makeAdminURL('/system/triple-whale/attribution/columns'),
            accounts: makeAdminURL('/system/triple-whale/attribution/accounts'),
            export: makeAdminURL('/system/triple-whale/attribution/export')
        }
    },
    kcp: {
        account: {
            list: makeAdminURL('/kcp/accounts'),
            get: (id) => makeAdminURL(`/kcp/accounts/${id}`),
            put: (id) => makeAdminURL(`/kcp/accounts/${id}`),
            productVisibility: (id) => makeAdminURL(`/kcp/accounts/${id}/product-visibility`),
            productSearch: (id) => makeAdminURL(`/kcp/accounts/${id}/products`),
            getCSP: (id) => makeAdminURL(`/kcp/accounts/${id}/csp`),
            shippingRates: (id) => makeAdminURL(`/kcp/accounts/${id}/shipping-rates`)
        },
        csp: {
            list: makeAdminURL('/kcp/csp'),
            refreshCache: makeAdminURL('/kcp/csp/refresh-cache'),
            updateBrandShipping: (id) => makeAdminURL(`/kcp/csp/update-shipping/${id}`),
            recalculateKlicklyPrice: makeAdminURL('/kcp/csp/recalculate-klickly-price'),
            explorer: makeAdminURL('/kcp/csp/explorer'),
            addRequest: (id) => makeAdminURL(`/kcp/csp/add-request/${id}`),
            get: (id) => makeAdminURL(`/kcp/csp/${id}`),
            create: makeAdminURL('/kcp/csp'),
            update: (id) => makeAdminURL(`/kcp/csp/${id}`),
            remove: (id) => makeAdminURL(`/kcp/csp/${id}`)
        },
        customers: {
            fetchUrl: makeAdminURL('/kcp/customers'),
            patch: (id) => makeAdminURL(`/kcp/customers/${id}`),
            remove: (id) => makeAdminURL(`/kcp/customers/${id}`),
            block: (id) => makeAdminURL(`/kcp/customers/${id}/block`),
            blockReason: (id) => makeAdminURL(`/kcp/customers/${id}/block-reason`),
            unblock: (id) => makeAdminURL(`/kcp/customers/${id}/unblock`),
            approve: (id) => makeAdminURL(`/kcp/customers/${id}/approve`),
            changeInviteTaskAttempts: (id) => makeAdminURL(`/kcp/customers/${id}/change-invite-task-attempts`)
        },
        inviteCodes: {
            fetchUrl: makeAdminURL('/kcp/invite-codes')
        },
        tasks: {
            fetchUrl: makeAdminURL('/kcp/tasks'),
            statistic: makeAdminURL('/kcp/tasks/statistics')
        },
        reviews: {
            accounts: makeAdminURL('/kcp/reviews/accounts'),
            list: makeAdminURL('/kcp/reviews'),
            get: (id) => makeAdminURL(`/kcp/reviews/${id}`),
            patch: (id) => makeAdminURL(`/kcp/reviews/${id}`)
        },
        rewards: makeAdminURL('/kcp/rewards'),
        upsells: {
            fetchUrl: makeAdminURL('/kcp/upsells')
        }
    },
    publishers: {
        fetchUrl: makeAdminURL('/publishers')
    },
    orders: {
        referral: {
            list: makeAdminURL('/orders/referral'),
            markAsPaid: (id) => makeAdminURL(`/orders/referral/${id}`)
        },
        pendingReview: {
            list: makeAdminURL('/orders-pending-review/list'),
            accountList: (accountId) => makeAdminURL(`/orders-pending-review/list/${accountId}`),
            proceed: (id) => makeAdminURL(`/orders-pending-review/proceed/${id}`),
            refund: (id) => makeAdminURL(`/orders-pending-review/refund/${id}`)
        }
    },
    notifications: {
        condition: {
            fetchUrl: makeAdminURL('/notifications/condition'),
            constants: makeAdminURL('/notifications/condition/get/constants'),
            textData: (id) => makeAdminURL(`/notifications/condition/${id}/text-data`),
            selectList: makeAdminURL('/notifications/condition/get/select-list'),
            audience: makeAdminURL('/notifications/condition/get/audience'),
            audienceCSV: makeAdminURL('/notifications/condition/get/audience-csv')
        },
        mailingList: {
            fetchUrl: makeAdminURL('/notifications/mailing-list'),
            audienceList: (id) => makeAdminURL(`/notifications/mailing-list/${id}/audience-list`),
            selectList: makeAdminURL('/notifications/mailing-list/get/select-list'),
            fileUpload: makeAdminURL('/notifications/mailing-list/file/upload')
        },
        template: {
            fetchUrl: makeAdminURL('/notifications/template'),
            selectList: makeAdminURL('/notifications/template/get/select-list'),
            preview: (id) => makeAdminURL(`/notifications/template/${id}/preview`),
            testIt: (id) => makeAdminURL(`/notifications/template/${id}/test-it`)
        },
        campaign: {
            fetchUrl: makeAdminURL('/notifications/campaign'),
            launch: (id) => makeAdminURL(`/notifications/campaign/${id}/launch`),
            stop: (id) => makeAdminURL(`/notifications/campaign/${id}/stop`)
        },
        automation: {
            fetchUrl: makeAdminURL('/notifications/automation')
        },
        serviceMail: {
            fetchUrl: makeAdminURL('/notifications/service-mail')
        },
        trigger: {
            fetchUrl: makeAdminURL('/notifications/trigger'),
            selectList: makeAdminURL('/notifications/trigger/get/select-list'),
            testIt: (id) => makeAdminURL(`/notifications/trigger/${id}/test-it`)
        },
        banList: {
            fetchUrl: makeAdminURL('/notifications/ban-list'),
            fileUpload: makeAdminURL('/notifications/ban-list/file/upload')
        },
        monitor: makeAdminURL('/notifications/monitor')
    },
    advertise: {
        generators: makeAdminURL('/advertise-generators'),
        strategies: makeAdminURL('/advertise-strategies')
    },
    reports: {
        generate: makeAdminURL('/reports')
    },
    customerPrivacy: {
        sendRequest: makeAdminURL('/customer-privacy'),
        optOut: makeAdminURL('/customer-privacy/opt-out')
    },
    lambda: {
        performanceJobs: makeAdminURL('/lambda/sls-cron/performance-jobs')
    },
    workers: {
        monitor: makeAdminURL('/workers/monitor')
    },
    dsp: {
        bidRequest: makeAdminURL('/dsp/bid-request')
    }
};

export const kcpAPI = {
    marketplace: {
        search: {
            basic: makeKcpURL('/marketplace/search')
        }
    }
};

export const adminAPIV2 = {
    account: {
        performanceCleanUp: (accountId) => makeAdminURLV2(`/accounts/performance-clean-up/${accountId}`),
        updateScriptTags: makeAdminURLV2('/accounts/update-script-tags'),
        updateWebPixel: makeAdminURLV2('/accounts/update-web-pixel'),
        invoiceRecalculate: (id) => makeAdminURLV2(`/accounts/${id}/invoices/recalculate`),
        reports: (accountId) => makeAdminURLV2(`/accounts/${accountId}/reports`)
    },
    brief: {
        preLive: makeAdminURLV2('/brief/pre-live'),
        pageSpeed: makeAdminURLV2('/brief/pre-live/pagespeed'),
        pageSpeedResult: (url) => makeAdminURLV2(`/brief/pre-live/pagespeed?url=${encodeURIComponent(url)}`),
        scriptPixel: makeAdminURLV2('/brief/day1/script-pixel'),
        orderSources: makeAdminURLV2('/brief/day1/order-sources'),
        gmv: makeAdminURLV2('/brief/day1/gmv'),
        widgetBasis: makeAdminURLV2('/brief/day14/widget-basis'),
        dspCreatives: makeAdminURLV2('/brief/day14/dsp-creatives'),
        customLandingPages: makeAdminURLV2('/brief/day14/custom-landing-pages'),
        purchaseEventGap: makeAdminURLV2('/brief/day14/purchase-event-gap'),
        orderBreakdown: makeAdminURLV2('/brief/day14/order-breakdown'),
        sendSlack: makeAdminURLV2('/brief/send-slack'),
        historyList: withQueryParams(makeAdminURLV2('/brief/history'), ['accountId', 'stage', 'limit']),
        ignoredApps: makeAdminURLV2('/brief/ignored-apps'),
        ignoredAppDelete: (id) => makeAdminURLV2(`/brief/ignored-apps/${id}`),
        // Per-account Shopify orders/create capture (flag + source allowlist).
        orderCapture: makeAdminURLV2('/brief/order-capture'),
        orderCaptureForAccount: withQueryParams(makeAdminURLV2('/brief/order-capture'), ['accountId']),
        orderCaptureSources: makeAdminURLV2('/brief/order-capture/sources'),
        orderCaptureSourceDelete: withQueryParams(makeAdminURLV2('/brief/order-capture/sources'), [
            'accountId',
            'sourceName'
        ]),
        orderCaptureSubscription: withQueryParams(makeAdminURLV2('/brief/order-capture/subscription'), ['accountId']),
        // Pre-live and day-1 page inspection runs in the Klickly Brief Chrome
        // extension; these back the install prompt and the run/poll cycle.
        extensionVersion: makeAdminURLV2('/brief/extension/version'),
        extensionDownload: makeAdminURLV2('/brief/extension/download'),
        extensionRun: (runUuid) => makeAdminURLV2(`/brief/extension/runs/${runUuid}`)
    },
    awaExplorer: {
        deleteEvents: () => makeAdminURLV2('/awa-explorer/delete-events'),
        deleteGroupedData: () => makeAdminURLV2('/awa-explorer/delete-grouped-data'),
        fillGrouped: () => makeAdminURLV2('/awa-explorer/fill-grouped'),
        insertEvents: () => makeAdminURLV2('/awa-explorer/insert-events'),
        rerunAwa: () => makeAdminURLV2('/awa-explorer/rerun-awa'),
        eventsToControl: withQueryParams(makeAdminURLV2('/awa-explorer/events-to-control'), [
            'accountId',
            'size',
            'designId'
        ]),
        ctrForPreviousRound: withQueryParams(makeAdminURLV2('/awa-explorer/ctr-for-previous-round'), [
            'accountId',
            'start',
            'end'
        ]),
        lastRoundStats: withQueryParams(makeAdminURLV2('/awa-explorer/last-round-stats'), [
            'mode',
            'accountId',
            'size'
        ]),
        makeBrandIndividual: () => makeAdminURLV2('/awa-explorer/make-brand-individual')
    },
    dsp: {
        creatives: {
            list: makeAdminURLV2('/dps/creatives')
        }
    }
};
